import React, { CSSProperties, useCallback, useMemo } from "react";
import styled from "styled-components";
import { zIndex_placedNote, zIndex_selectedNote, zIndex_clickedNote, InstrumentInstruction, SubdivisionType, NoteId } from "../consts";
import { BEAT_WIDTH } from "../contexts/CompositionContextProvider";

const StyledNote = styled.div<{
  $width: number,
  $top: number,
  $left: number,
  $bgColor: string,
  $shouldMouseIgnoreMe?: boolean,
  $isClickedNote?: boolean,
  $isNoteSelected?: boolean,
}>`
  cursor: ${({ $shouldMouseIgnoreMe }) => $shouldMouseIgnoreMe ? 'default' : 'pointer' };
  width: ${({ $width }) => `${$width}px`};
  height: 13px;
  content: " ";
  background-color: ${({ $bgColor }) => $bgColor};
  position: absolute;
  left: ${({ $left }) => `${$left}px` };
  top: ${({ $top }) => `${$top}px` };
  z-index: ${({ $isClickedNote, $isNoteSelected }) => $isClickedNote
    ? zIndex_clickedNote
    : $isNoteSelected
      ? zIndex_selectedNote
      : zIndex_placedNote };
  border-radius: 0;
  box-shadow: ${({ $isClickedNote, $isNoteSelected }) => `0px 0px 0px ${
    $isNoteSelected ? '1px blue' : '1px black'
  }${
    $isNoteSelected
      ? $isClickedNote ? '' : ', 2px 2px 0px 0px blue'
      : $isClickedNote ? ', 2px 2px 0px 0px black' : ''
  }` };
  pointer-events: ${({ $shouldMouseIgnoreMe, $isClickedNote }) => $shouldMouseIgnoreMe || $isClickedNote ? 'none' : 'unset' };
`;

const BEAT_HEIGHT = 14;
export function PlacedNote({
  children,
  topmostMidiNote,
  bgColor,
  instrumentInstruction,
  shouldMouseIgnoreMe,
  onMouseDown,
  isClickedNote,
  isNoteSelected,
  style,
}: {
  children?: React.ReactNode
  topmostMidiNote: number;
  bgColor: string;
  instrumentInstruction: InstrumentInstruction,
  shouldMouseIgnoreMe?: boolean;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, noteId: NoteId) => void
  isClickedNote?: boolean;
  isNoteSelected?: boolean;
  style?: CSSProperties
}) {
  const { midiBeat, midiNote, noteWidth, subdivisionType } = instrumentInstruction;
  const beatWidth = subdivisionType === SubdivisionType.q ? 15 : 20;
  
  const shouldWiggleShift = ((isClickedNote && !isNoteSelected) || (isNoteSelected && !isClickedNote));
  const topShift = shouldWiggleShift ? -1 : 0
  const y = (topmostMidiNote - midiNote) * BEAT_HEIGHT;
  const top = y + 1 + topShift;

  const leftShift = shouldWiggleShift ? -1 : 0;
  const x = (midiBeat - 1) * BEAT_WIDTH;
  const tripletLeftShift = subdivisionType === SubdivisionType.t ? 5 * ((midiBeat - 1) % 4) : 0;
  const left = x + 1 + leftShift + tripletLeftShift;
  const noteId = useMemo(() => instrumentInstruction.noteId, [instrumentInstruction.noteId]);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => onMouseDown?.(e, noteId),
    [onMouseDown, noteId]
  );
  return (
    <StyledNote
      $bgColor={bgColor}
      $isNoteSelected={isNoteSelected}
      $top={top}
      $left={left}
      $width={noteWidth * beatWidth - 1}
      $shouldMouseIgnoreMe={shouldMouseIgnoreMe}
      $isClickedNote={isClickedNote}
      onMouseDown={handleMouseDown}
      style={style}>
      {children}
    </StyledNote>
  );
}